import { getRequestHeaders, handleRes, logError } from 'utils';
import { Job, Location } from 'types';

export interface GetJobsResponse extends Response {
  jobs?: Array<Job>;
}
export interface GetJobResponse extends Response {
  job?: Job;
}
export interface CreateJobResponse extends Response {
  id?: number;
}

export interface LocationResponse extends Response {
  location?: Location;
}
export interface DoNotTowRequestResponse extends Response {
  canBeTowed?: boolean;
}

export const createJob = async (
  tenantID: number,
  body: string, // CreateJobBody,
): Promise<CreateJobResponse> => {
  return await fetch(`${window.JOB_BASE_URL}/tenant/${tenantID}/jobs`, {
    method: 'POST',
    ...getRequestHeaders(),
    body,
  });
};

export const getJobByID = (tenantID: number, jobID: number): Promise<Job> => {
  return fetch(`${window.JOB_BASE_URL}/tenant/${tenantID}/jobs/${jobID}`, {
    method: 'GET',
    ...getRequestHeaders(),
  })
    .then((res) => handleRes<Job>(res, 200))
    .catch((e) => {
      throw e;
    });
};

interface GetJobsParams {
  tenantID: number;
  propertyID?: number;
  propertyGroupID?: number;
  start: string; // ISO 8601
  end: string; // ISO 8601
}
export const getTenantJobs = ({
  tenantID,
  start,
  end,
}: GetJobsParams): Promise<Job[]> => {
  return fetch(
    `${window.JOB_BASE_URL}/tenant/${tenantID}/jobs?start=${start}&end=${end}`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<Job[]>(res, 200))
    .catch((e) => {
      logError(e);
      return [];
    });
};

export const getPropertyJobs = ({
  tenantID,
  propertyID,
  start,
  end,
}: GetJobsParams): Promise<Job[]> => {
  return fetch(
    `${window.JOB_BASE_URL}/tenant/${tenantID}/property/${propertyID}/jobs?start=${start}&end=${end}`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<Job[]>(res, 200))
    .catch((e) => {
      logError(e);
      return [];
    });
};

export const getPropertyGroupJobs = ({
  tenantID,
  propertyGroupID,
  start,
  end,
}: GetJobsParams): Promise<Job[]> => {
  return fetch(
    `${window.JOB_BASE_URL}/tenant/${tenantID}/propertyGroup/${propertyGroupID}/jobs?start=${start}&end=${end}`,
    {
      method: 'GET',
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<Job[]>(res, 200))
    .catch((e) => {
      logError(e);
      return [];
    });
};

export const createJobLocation = async (
  tenantID: number,
  body: Location,
): Promise<LocationResponse> => {
  return await fetch(
    `${window.JOB_BASE_URL}/tenant/${tenantID}/jobs/location`,
    {
      method: 'POST',
      ...getRequestHeaders(),
      body: JSON.stringify(body),
    },
  );
};

export const canTowVehicle = async (
  tenantID: number,
  body: string, // DoNotTowRequest,
): Promise<Response> => {
  return await fetch(
    `${window.PROPERTY_BASE_URL}/tenant/${tenantID}/canVehicleBeTowed`,
    {
      method: 'POST',
      body,
      ...getRequestHeaders(),
    },
  );
};

export interface ApproveJobParams {
  tenantID: number;
  jobID: number;
  body: string; // Signature FileLink
}
export const approveJob = async ({
  tenantID,
  jobID,
  body,
}: ApproveJobParams): Promise<Response> => {
  return await fetch(
    `${window.JOB_BASE_URL}/tenant/${tenantID}/jobs/${jobID}/approve`,
    {
      method: 'PUT',
      ...getRequestHeaders(),
      body,
    },
  );
};

export interface DenyJobParams {
  tenantID: number;
  jobID: number;
  reason: string; // Rejection Reason
}
export const denyJob = async ({
  tenantID,
  jobID,
  reason,
}: DenyJobParams): Promise<Response> => {
  return await fetch(
    `${window.JOB_BASE_URL}/tenant/${tenantID}/jobs/${jobID}/deny`,
    {
      method: 'PUT',
      ...getRequestHeaders(),
      body: JSON.stringify({ reason }),
    },
  );
};

export interface CancelJobParams {
  tenantID: number;
  jobID: number;
  reasonID: number; // TMS cancel reason key
  reason: string; // Cancellation reason description
}
export const cancelJob = async ({
  tenantID,
  jobID,
  reasonID,
  reason,
}: CancelJobParams): Promise<Response> => {
  return await fetch(
    `${window.JOB_BASE_URL}/tenant/${tenantID}/jobs/${jobID}/cancel`,
    {
      method: 'PUT',
      ...getRequestHeaders(),
      body: JSON.stringify({ reasonID, reason }),
    },
  );
};

export interface CancelReason {
  key: number;
  description: string;
}
export interface GetCancelReasonsResponse {
  cancelReasons: CancelReason[];
}
export const getCancelReasons = (
  tenantID: number,
): Promise<GetCancelReasonsResponse> => {
  return fetch(`${window.JOB_BASE_URL}/tenant/${tenantID}/jobs/cancelReasons`, {
    method: 'GET',
    ...getRequestHeaders(),
  })
    .then((res) => handleRes<GetCancelReasonsResponse>(res, 200))
    .catch((_) => {
      return { cancelReasons: [] };
    });
};
